import { Select, SelectProps, useMantineTheme } from "@mantine/core";
import { ArrowDown2 } from "iconsax-react";
import { useMyPlans } from "../../../../hooks/useMyPlans";
import { IQueryPosition } from "../../../../interfaces/IQuery";
import CustomSelectItem from "./CustomSelectItem";
import { useAuth } from "../../../../context/auth";
import { useMyProducts } from "../../../../hooks/useMyProducts";
import { ERole } from "../../../../interfaces/User/newUser";
import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { EServiceType } from "../../../../interfaces/MyPlans/IMyPlans";

interface ProductsSelect {
  value: string;
  label: string;
  isChecked: boolean;
}
interface IProps extends Omit<SelectProps, "data" | "value" | "onChange"> {
  query: IQueryPosition;
  setQuery: React.Dispatch<React.SetStateAction<IQueryPosition>>;
}
interface IProduct {
  id: number;
  title: string;
}

const MultiSelectProducts = ({ query, setQuery, ...props }: IProps) => {
  const theme = useMantineTheme();
  const { roleSelected } = useAuth();
  const { t, i18n } = useTranslation();
  const roleSelectedIsAnalyst = roleSelected === ERole.analyst;
  const roleSelectedIsClient = roleSelected === ERole.client;

  const { data: dataMyProducts, isFetching: isLoadingMyProducts } =
    useMyProducts({
      enabled: roleSelectedIsAnalyst,
      active: EServiceType.All,
    });

  const { data: dataMyPlans, isFetching: isLoadingMyPlans } = useMyPlans({
    status: "",
    enabled: roleSelectedIsClient,
  });

  const [products, setProducts] = useState<ProductsSelect[]>(
    [] as ProductsSelect[]
  );

  const isFirstRenderRef = useRef(true);

  const hasData =
    (!!dataMyPlans && dataMyPlans.dates.length > 0) || !!dataMyProducts;

  const loading = isLoadingMyProducts || isLoadingMyPlans;

  useEffect(() => {
    // console.log("🚀 ~ passou pelo useEffect:", hasData);

    if (hasData) {
      const rawProducts: IProduct[] = roleSelectedIsAnalyst
        ? dataMyProducts?.data ?? []
        : dataMyPlans?.dates?.flatMap((date) =>
            date.user_plans.map((plan) => plan.product)
          ) ?? [];

      const productsData: IProduct[] = Array.from(
        new Map(rawProducts.map((product) => [product.id, product])).values()
      );

      let arrayProducts: ProductsSelect[] = [];

      if (productsData.length === 1) {
        // Para um único serviço, sempre seleciona automaticamente
        setQuery((prev) => ({
          ...prev,
          productId: [productsData[0].id],
        }));
        arrayProducts = [
          {
            value: productsData[0].id.toString(),
            label: productsData[0].title,
            isChecked: true,
          },
        ];
      } else {
        // Para múltiplos serviços, preserva a seleção atual ou define estado inicial
        const currentSelectedIds = query.productId || [];
        const hasCurrentSelection = currentSelectedIds.length > 0;

        // Verifica se todos os serviços atuais estão selecionados
        const allCurrentProductsSelected =
          currentSelectedIds.length > 0 &&
          productsData.every((product) =>
            currentSelectedIds.includes(product.id)
          );

        // Verifica se nenhum serviço está selecionado
        const noProductsSelected =
          currentSelectedIds.length === 0 ||
          !productsData.some((product) =>
            currentSelectedIds.includes(product.id)
          );

        arrayProducts = [
          {
            value: "",
            label: t("operacoes.mock.all_products"),
            isChecked: allCurrentProductsSelected || noProductsSelected,
          },
          ...productsData.map((product) => ({
            value: product.id.toString(),
            label: product.title,
            isChecked: hasCurrentSelection
              ? currentSelectedIds.includes(product.id)
              : false,
          })),
        ];

        // Se não há seleção atual e NÃO é a primeira carga, mantém o estado atual
        if (!hasCurrentSelection && !isFirstRenderRef.current) {
          // console.log("🚀 ~ !hasCurrentSelection && não é primeira carga");
          return; // Não atualiza o estado para preservar a seleção do usuário
        }
      }

      setProducts(arrayProducts);
      isFirstRenderRef.current = false;
    } else {
      setProducts([
        { value: "", label: t("operacoes.mock.no_products"), isChecked: true },
      ]);
    }
  }, [hasData, loading]);

  // Sincroniza o estado dos serviços quando a query.productId muda externamente
  useEffect(() => {
    if (products.length > 1) {
      // Só sincroniza se há múltiplos serviços
      const currentSelectedIds = query.productId || [];

      const updatedProducts = products.map((product) => {
        if (product.value === "") {
          // Para o item "todos", verifica se todos os serviços estão selecionados
          const allSelected =
            currentSelectedIds.length > 0 &&
            products
              .filter((p) => p.value !== "")
              .every((p) => currentSelectedIds.includes(Number(p.value)));
          return { ...product, isChecked: allSelected };
        } else {
          // Para serviços individuais, verifica se estão na seleção atual
          return {
            ...product,
            isChecked: currentSelectedIds.includes(Number(product.value)),
          };
        }
      });

      // Só atualiza se houve mudança real
      const hasChanges = updatedProducts.some(
        (product, index) => product.isChecked !== products[index].isChecked
      );

      if (hasChanges) {
        setProducts(updatedProducts);
      }
    }
  }, [query.productId]);

  const updateProductSelection = (
    products: ProductsSelect[],
    value: string
  ) => {
    let updated = [...products];

    if (value === "") {
      return updated.map((p) => ({ ...p, isChecked: p.value === "" }));
    }

    updated = updated.map((p) => ({
      ...p,
      isChecked:
        p.value === "" ? false : p.value === value ? !p.isChecked : p.isChecked,
    }));
    const individual = updated.filter((p) => p.value !== "");
    const allSelected =
      individual.length > 0 && individual.every((p) => p.isChecked);
    const noneSelected =
      individual.length > 0 && individual.every((p) => !p.isChecked);

    return allSelected || noneSelected
      ? updated.map((p) => ({ ...p, isChecked: p.value === "" }))
      : updated;
  };

  const handleSelectChange = (value: string | null) => {
    if (products.length === 1) return;
    if (value === null) return;

    const updatedProducts = updateProductSelection(products, value);
    const selectedIds = updatedProducts
      .filter((p) => p.isChecked && p.value !== "")
      .map((p) => Number(p.value));

    setProducts(updatedProducts);
    setQuery((prev) => ({
      ...prev,
      productId: selectedIds.length > 0 ? selectedIds : undefined,
    }));
  };

  const selectedProducts = products.filter(
    (p) => p.isChecked && p.value !== ""
  );

  const selectDisplayValue = useMemo(() => {
    if (products.length === 1) return products[0].label;
    if (selectedProducts.length >= 1) {
      return t("operacoes.multi_select_products.selected_products", {
        qtd: selectedProducts.length,
      });
    }
    return t("operacoes.mock.all_products");
  }, [selectedProducts, products]);

  return (
    <Select
      mt={-8}
      styles={{
        rightSection: { pointerEvents: "none" },
        input: { color: theme.colors.basic[5] },
        root: { zIndex: 1000 },
      }}
      aria-label="select"
      dropdownPosition="bottom"
      rightSection={<ArrowDown2 size="24" color={theme.colors.basic[5]} />}
      label={t("operacoes.multi_select_products.select_products")}
      placeholder={
        loading
          ? t("operacoes.multi_select_products.loading_products")
          : selectDisplayValue
      }
      maw={{ base: "100%", 780: "280px" }}
      w="100%"
      searchable
      disabled={!hasData || loading}
      data={products}
      value={selectDisplayValue}
      onChange={handleSelectChange}
      itemComponent={CustomSelectItem}
      {...props}
    />
  );
};

export default MultiSelectProducts;
